import { useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import {
  useCollectionsStore,
  setCollections,
  setSelectedCollection,
  setSelectedRequest,
  setSelectedFolder,
  setEditingItem,
} from '../store/collectionsStore';
import { useTabsStore, setRequestTabs, setCollectionTabs } from '../store/tabsStore';
import { isTabFor } from '../utils/collectionTabState';
import { apiRequestService } from '../services/apiRequestService';
import { collectionService } from '../services/collectionService';
import { renameFolderInCollection } from '../utils/collectionFolders';
import { isBackendWorkspace } from '../utils/workspaceStorage';

/**
 * Sidebar rename: start/draft/commit/cancel for request, collection and folder
 * names, plus the in-editor request-name commit. Cross-domain reads via
 * getState(); the rename snapshot ref is shared with the App keyboard-shortcut
 * effect, so it's passed in. scheduleTreeSave (folder rename persistence) stays
 * in App (useCollections) and is passed in.
 *
 * @param {{ toastT: Function, renameSnapshotRef: { current: any }, scheduleTreeSave: Function }} deps
 */
export function useSidebarRename({ toastT, renameSnapshotRef, scheduleTreeSave }) {
  const applySidebarRenameDraft = useCallback((type, id, draft, { syncOpenTabs = true } = {}) => {
    if (type === 'folder') {
      setCollections((prev) =>
        prev.map((c) => {
          const folders = c.settings?.folders;
          if (!folders?.some((f) => String(f.id) === String(id))) return c;
          return renameFolderInCollection(c, id, draft);
        })
      );
      return;
    }

    if (type === 'collection') {
      setCollections((prev) =>
        prev.map((c) => (String(c.id) === String(id) ? { ...c, name: draft } : c))
      );
      setSelectedCollection((prev) =>
        prev && String(prev.id) === String(id) ? { ...prev, name: draft } : prev
      );
      setCollectionTabs((prev) =>
        prev.map((tab) =>
          isTabFor(tab, id) ? { ...tab, name: draft } : tab
        )
      );
      return;
    }

    setCollections((prev) =>
      prev.map((c) => ({
        ...c,
        requests: (c.requests || []).map((r) =>
          String(r.id) === String(id) ? { ...r, name: draft } : r
        ),
      }))
    );
    if (!syncOpenTabs) return;
    setSelectedRequest((prev) =>
      prev && String(prev.id) === String(id) ? { ...prev, name: draft } : prev
    );
    setRequestTabs((prev) =>
      prev.map((tab) =>
        String(tab.originalId) === String(id) || String(tab.id) === String(id)
          ? { ...tab, name: draft }
          : tab
      )
    );
  }, []);

  const handleStartSidebarRename = useCallback((type, item) => {
    renameSnapshotRef.current = { type, id: item.id, previousName: item.name };
    setEditingItem({ type, id: item.id });
    if (type === 'collection') {
      setSelectedCollection(item);
      setSelectedRequest(null);
      setSelectedFolder(null);
    } else if (type === 'folder') {
      setSelectedFolder({ ...item, collectionId: item.collectionId });
      setSelectedRequest(null);
      setSelectedCollection(null);
    } else {
      setSelectedRequest(item);
      setSelectedCollection(null);
      setSelectedFolder(null);
    }
  }, [renameSnapshotRef]);

  const handleSidebarRenameDraft = useCallback((type, id, draft) => {
    // Live sidebar preview only — avoid pushing each keystroke into RequestBuilder
    // (that re-renders the URL field and steals focus from the rename input).
    applySidebarRenameDraft(type, id, draft, { syncOpenTabs: type !== 'request' });
  }, [applySidebarRenameDraft]);

  const persistRequestName = useCallback(async (requestId, trimmed, previousName) => {
    applySidebarRenameDraft('request', requestId, trimmed);
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;

    if (!isBackendWorkspace(currentWorkspace, isAuthenticated)) {
      return;
    }
    try {
      await apiRequestService.updateRequest(requestId, { name: trimmed });
    } catch (error) {
      console.error('Failed to rename request:', error);
      toastT('toast.renameFailed', 'error', { message: error.message });
      applySidebarRenameDraft('request', requestId, previousName);
      throw error;
    }
  }, [toastT, applySidebarRenameDraft]);

  const handleCommitRequestName = useCallback(async (newName, previousNameFromEdit) => {
    const trimmed = (newName || '').trim();
    if (!trimmed) return;

    const { requestTabs, activeRequestTabId } = useTabsStore.getState();
    const tab = requestTabs.find((t) => t.id === activeRequestTabId);
    if (!tab) return;

    const previousName = previousNameFromEdit ?? tab.name ?? 'New Request';
    if (trimmed === previousName.trim()) return;

    const requestId = tab.originalId ?? (!tab.isNewRequest ? tab.id : null);

    if (requestId) {
      try {
        await persistRequestName(requestId, trimmed, previousName);
        toastT('toast.requestRenamed', 'success');
      } catch {
        // Reverted in persistRequestName
      }
      return;
    }

    setRequestTabs((prev) =>
      prev.map((t) =>
        t.id === useTabsStore.getState().activeRequestTabId ? { ...t, name: trimmed } : t
      )
    );
  }, [toastT, persistRequestName]);

  const handleSaveSidebarRename = useCallback(async (type, id, newName) => {
    const trimmed = newName.trim();
    const snap = renameSnapshotRef.current;
    renameSnapshotRef.current = null;
    setEditingItem(null);

    if (!trimmed) {
      if (snap) applySidebarRenameDraft(snap.type, snap.id, snap.previousName);
      return;
    }

    const previousName = snap?.previousName ?? trimmed;
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;

    try {
      if (type === 'collection') {
        applySidebarRenameDraft(type, id, trimmed);
        if (isBackendWorkspace(currentWorkspace, isAuthenticated)) {
          await collectionService.updateCollection(id, { name: trimmed });
        }
        toastT('toast.collectionRenamed', 'success');
      } else if (type === 'request') {
        if (trimmed !== previousName.trim()) {
          await persistRequestName(id, trimmed, previousName);
          toastT('toast.requestRenamed', 'success');
        } else {
          applySidebarRenameDraft('request', id, previousName);
        }
      } else if (type === 'folder') {
        const collections = useCollectionsStore.getState().collections;
        const col = collections.find((c) =>
          (c.settings?.folders || []).some((f) => String(f.id) === String(id))
        );
        if (col) {
          const next = renameFolderInCollection(col, id, trimmed);
          const nextCollections = collections.map((c) =>
            String(c.id) === String(col.id) ? next : c
          );
          setCollections(nextCollections);
          scheduleTreeSave(nextCollections);
          toastT('toast.folderRenamed', 'success');
        }
      }
    } catch (error) {
      if (type === 'collection') {
        console.error('Failed to rename:', error);
        toastT('toast.renameFailed', 'error', { message: error.message });
        if (snap) applySidebarRenameDraft(snap.type, snap.id, snap.previousName);
      }
    }
  }, [toastT, applySidebarRenameDraft, persistRequestName, scheduleTreeSave, renameSnapshotRef]);

  const handleCancelSidebarRename = useCallback(() => {
    const snap = renameSnapshotRef.current;
    if (snap) applySidebarRenameDraft(snap.type, snap.id, snap.previousName);
    renameSnapshotRef.current = null;
    setEditingItem(null);
  }, [applySidebarRenameDraft, renameSnapshotRef]);

  return {
    applySidebarRenameDraft,
    handleStartSidebarRename,
    handleSidebarRenameDraft,
    persistRequestName,
    handleCommitRequestName,
    handleSaveSidebarRename,
    handleCancelSidebarRename,
  };
}
